import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import { BRAND, NAV } from "@/lib/site";
import { MegaMenu } from "@/components/MegaMenu";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobile(false); setOpen(null); }, [pathname]);

  return (
    <header
      data-testid="site-navbar"
      onMouseLeave={() => setOpen(null)}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,backdrop-filter] duration-500 ${scrolled || open || mobile ? "glass shadow-[0_8px_40px_-20px_rgba(19,34,56,.35)]" : "bg-transparent border-b border-transparent"}`}
    >
      <div className="container-x flex h-[76px] items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo-link" className="group flex items-center gap-3 shrink-0">
          <img src={BRAND.logo} alt={BRAND.name} className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-[var(--mint)]/60 shadow-[0_6px_20px_-8px_rgba(19,34,56,.4)] transition-transform duration-500 group-hover:scale-105" />
          <div className="leading-tight">
            <div className="font-display text-lg sm:text-xl font-bold tracking-tight text-[var(--navy)]">Starke United</div>
            <div className="font-mono text-[0.6rem] uppercase tracking-[0.24em] text-[var(--mint)]">Global Resources LLP</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-desktop">
          {NAV.map((item) => {
            const hasPanel = item.mega || item.children;
            const isOpen = open === item.label;
            return (
              <div key={item.label} className="relative" onMouseEnter={() => setOpen(hasPanel ? item.label : null)}>
                {item.to ? (
                  <Link to={item.to} className={`nav-link ${pathname === item.to ? "active" : ""}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>{item.label}</Link>
                ) : (
                  <button className={`nav-link inline-flex items-center gap-1 ${isOpen ? "active" : ""}`} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {item.label} <ChevronDown size={14} className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                  </button>
                )}
                <AnimatePresence>
                  {isOpen && item.children && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} transition={{ duration: 0.25 }}
                      className="absolute left-0 top-full mt-3 min-w-[220px] rounded-2xl glass p-2 shadow-xl"
                    >
                      {item.children.map((c) => (
                        <Link key={c.label} to={c.to} data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center justify-between rounded-xl px-4 py-2.5 text-sm font-medium text-[var(--navy)] hover:bg-[var(--teal-soft)] transition-colors">
                          {c.label} <ArrowUpRight size={14} className="text-[var(--mint)]" />
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/#contact" data-testid="nav-quote-btn" className="btn-pill btn-navy !hidden md:!inline-flex !py-2.5 !px-5 text-xs">
            Request a Quote <ArrowUpRight size={14} className="arrow" />
          </Link>
          <button data-testid="nav-mobile-toggle" className="lg:hidden grid h-11 w-11 place-items-center rounded-full border border-slate-200 bg-white/70" onClick={() => setMobile((m) => !m)} aria-label="Menu">
            {mobile ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>{open === "Our Collection" && <MegaMenu onClose={() => setOpen(null)} />}</AnimatePresence>

      <AnimatePresence>
        {mobile && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.35 }} className="lg:hidden overflow-hidden border-t border-slate-200" data-testid="nav-mobile-menu">
            <div className="container-x py-4 flex flex-col gap-1 max-h-[75vh] overflow-y-auto" data-lenis-prevent>
              {[{ label: "Home", to: "/" }, { label: "Company Profile", to: "/#about" }, { label: "Our Collection", to: "/collection" }, { label: "Across the Globe", to: "/#global" }, { label: "E-Catalogue", to: "/#catalogues" }, { label: "Tiles Calculator", to: "/#calculator" }, { label: "Contact Us", to: "/#contact" }].map((l) => (
                <Link key={l.label} to={l.to} className="py-3 text-base font-semibold text-[var(--navy)] border-b border-slate-100" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
              ))}
              <Link to="/#contact" className="btn-pill btn-navy mt-4 justify-center">Request a Quote</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
